import { HierarchyUtils, Vector, ComponentIds, TransformUtils, Ray, MeasurementUtils, RaycastUtils, ModelUtils, ConnectionUtils } from '@inter-ikea-core/core';
import { AssemblyInstructionsService, RoomConnectorConstants } from '@inter-ikea-core/core';
import PlannerProductComponent from './components/PlannerProductComponent';
import ProductInfoComponent from './components/ProductInfoComponent';
import ShoppingBagComponent from './components/ShoppingBagComponent';
import OuterMeasurementsComponent from './components/OuterMeasurementsComponent';
import { PartKey, ProductIdentifiers } from '../types';
import { calculateDistanceBetweenPositions } from '../utils/calculateDistance';
import * as R from 'ramda';
import { AngledEntityComponent, RangeComponent, SeeThroughFrontComponent } from '..';
export const transformOuterMeasurements = (outsideMeasurements, rotate) => {
    const { width, height, depth, min, max } = outsideMeasurements;
    const minValues = { x: min.X, y: min.Y, z: min.Z };
    const maxValues = { x: max.X, y: max.Y, z: max.Z };
    return {
        width: rotate ? depth : width,
        height,
        depth: rotate ? width : depth,
        min: minValues,
        max: maxValues
    };
};
export class EntityApi {
    constructor(selectionService, core, placementService, ipexViewer, reportErrorToSentry, callbacks = {}) {
        this.getScene = () => {
            return this.core.services.sceneManager.scene;
        };
        this.entityIsPartOfAngledSolution = (entities) => {
            const entityAngled = entities.some(entity => {
                const worldRotation = entity
                    .getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)
                    ?.transform?.rotation?.toEulerDegrees();
                if (!worldRotation)
                    return false;
                return Math.abs(Math.round(worldRotation.Y) % 90) !== 0;
            });
            if (entityAngled) {
                return true;
            }
            let partOfAngledSolution = false;
            entities.forEach(entity => {
                const found = !!HierarchyUtils.findInUpwardsHierarchy(entity, e => e.hasComponent(AngledEntityComponent.id));
                if (found) {
                    partOfAngledSolution = true;
                }
            });
            return partOfAngledSolution;
        };
        this.getStackedEntities = (entity) => {
            const stackedFrameEntities = HierarchyUtils.descendants(entity).filter(child => child.hasComponent(ComponentIds.FRAME_COMPONENT_ID));
            return stackedFrameEntities
                .flatMap(frame => {
                return frame.hasComponent(ComponentIds.ASSEMBLY_STRUCTURE_COMPONENT_ID)
                    ? this.findEntityWithPartKey(HierarchyUtils.children(frame), PartKey.FRAME)
                    : frame;
            })
                .filter((e) => !!e);
        };
        this.core = core;
        this.selectionService = selectionService;
        this.assemblyInstructionsService = new AssemblyInstructionsService();
        this.placementService = placementService;
        this.ipexViewer = ipexViewer;
        this.reportErrorToSentry = reportErrorToSentry;
        this.callbacks = callbacks;
    }
    setIpexViewer(ipexViewer) {
        this.ipexViewer = ipexViewer;
    }
    /**
     * @returns the outer measurements of the solution.
     */
    getOuterMeasurements() {
        const entities = this.getAll().filter((entity) => {
            return !entity.hasComponent(ComponentIds.PROPPING_COMPONENT_ID);
        });
        if (entities.length === 0) {
            return null;
        }
        const entitiesToMeasure = this.getByComponent(OuterMeasurementsComponent.id);
        // Only calculate outer measurments if there are any entities in the scene that
        // should be measured
        if (entitiesToMeasure.length === 0) {
            return null;
        }
        const outsideMeasurements = MeasurementUtils.getOutsideMeasurementWithoutHierarchies(entitiesToMeasure);
        if (!outsideMeasurements) {
            return null;
        }
        return transformOuterMeasurements(outsideMeasurements);
    }
    /**
     * @returns the outer measurement entities grouped per wall
     */
    getOuterMeasurementEntitiesPerWall() {
        const entities = this.getByComponent(OuterMeasurementsComponent.id);
        if (entities.length === 0) {
            return null;
        }
        return entities.reduce((acc, entity) => {
            const wall = this.getEntityWall(entity);
            return {
                ...acc,
                [wall]: R.uniqBy(R.prop('id'), [...acc[wall], entity])
            };
        }, { front: [], left: [], right: [], back: [] });
    }
    /**
     * @returns the outer measurements of the solution per wall.
     */
    getOuterMeasurementsPerWall() {
        const groupedEntities = this.getOuterMeasurementEntitiesPerWall();
        if (!groupedEntities) {
            return null;
        }
        return Object.entries(groupedEntities).reduce((acc, [wall, entities]) => {
            const outsideMeasurements = MeasurementUtils.getOutsideMeasurementWithoutHierarchies(entities);
            if (!outsideMeasurements) {
                return acc;
            }
            const transformedMeasurements = transformOuterMeasurements(outsideMeasurements, wall === 'left' || wall === 'right');
            if (transformedMeasurements === null) {
                return acc;
            }
            return {
                ...acc,
                [wall]: transformedMeasurements
            };
        }, {});
    }
    getBoundingBox(entities) {
        const outsideMeasurements = MeasurementUtils.getOutsideMeasurement(entities);
        if (!outsideMeasurements || !outsideMeasurements.min || !outsideMeasurements.max) {
            return null;
        }
        const boundingBox = {
            max: {
                x: outsideMeasurements.max.X / 1000,
                y: outsideMeasurements.max.Y / 1000,
                z: outsideMeasurements.max.Z / 1000
            },
            min: {
                x: outsideMeasurements.min.X / 1000,
                y: outsideMeasurements.min.Y / 1000,
                z: outsideMeasurements.min.Z / 1000
            }
        };
        return boundingBox;
    }
    /**
     *
     * @param entity entity to get assembly instructions for
     * @returns assembly instructions for entity
     */
    getAssemblyInstructionsForEntity(entity) {
        return this.assemblyInstructionsService.getAssemblyInstructionsForPointSeries(entity);
    }
    /**
     * Selects an entity in the scene
     * @param entity entity to select
     */
    select(entity) {
        if (entity) {
            this.selectionService.toggle(entity);
        }
        else {
            this.unselect();
        }
    }
    /**
     * Unselects all selected entities
     * and removes lingering outlined components
     */
    unselect() {
        this.selectionService.unselectAll();
        const outlinedEntities = this.getByComponent(ComponentIds.OUTLINED_COMPONENT_ID);
        outlinedEntities.forEach(entity => {
            entity.removeComponent(ComponentIds.OUTLINED_COMPONENT_ID);
        });
    }
    /**
     * Deletes an entity from the scene
     * @param entity entity to be deleted
     */
    delete(entity) {
        ConnectionUtils.disconnectAndDisposeEntityAndHierarchy(entity);
    }
    /**
     * Deletes multiple entities from the scene
     * @param entities entities to be deleted
     */
    deleteMultiple(entities) {
        ConnectionUtils.disconnectAndDisposeEntitiesAndHierarchies(entities);
    }
    /**
     *
     * @param entity entity to duplicate
     * @returns placementresult for placing the clone in scene
     */
    duplicate(entity) {
        const worldPosition = TransformUtils.worldPosition(entity);
        const clone = HierarchyUtils.duplicateEntity(entity, this.core.services);
        let result = this.placementService.snapToPosition(clone, worldPosition, false);
        if (!result.placedEntity) {
            // If not possible to snap the clone to the position of the original, try to snap it to scene
            result = this.placementService.snapToScene(clone);
        }
        if (!result.placedEntity) {
            // If not possible to place the clone at all, dispose it
            this.core.services.entityManager.disposeEntity(clone);
        }
        return result;
    }
    /**
     * @param component Component to filter by
     * @returns all entities with provided component
     */
    getByComponent(componentId, range) {
        const scene = this.core.services.sceneManager;
        const entities = scene.entities
            .filter(entity => !entity.isDisposed)
            .filter((entity) => range ? entity.getComponent(RangeComponent.id)?.range === range : true)
            .filter((entity) => entity.hasComponent(componentId));
        return entities;
    }
    /**
     *
     * @param entity entity
     * @return productRef string
     */
    getProductRef(entity) {
        const productRef = entity.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)
            ?.productRef;
        return productRef;
    }
    /**
     * @param components list of components to filter by
     * @returns all entities that has at least on of the components passed as parameter
     */
    getByComponents(componentIds, range) {
        return this.core.services.sceneManager.entities
            .filter(entity => !entity.isDisposed)
            .filter((entity) => range ? entity.getComponent(RangeComponent.id)?.range === range : true)
            .filter((entity) => componentIds.some(componentId => entity.hasComponent(componentId)));
    }
    /**
     * @param entity
     * @returns productType for the given entity
     */
    getProductType(entity) {
        return entity.getComponent(ProductInfoComponent.id)?.productType || null;
    }
    /**
     * @param entity
     * @returns global article number for the given entity
     */
    getGlobalArticleNumber(entity) {
        return entity.getComponent(ProductInfoComponent.id)?.globalArticleNbr;
    }
    /**
     * @param entity
     * @param productType
     * @returns true if an entity is of specific product type
     */
    isProductType(entity, productType) {
        return this.isOneOfProductTypes(entity, [productType]);
    }
    /**
     * @param entity
     * @param productType
     * @returns true if an entity is one of product types
     */
    isOneOfProductTypes(entity, productTypes) {
        return productTypes.some(productType => this.getProductType(entity) === productType);
    }
    /**
     * @param productType Type to filter by
     * @returns all entities with provided product type
     */
    getByProductType(productType) {
        const scene = this.core.services.sceneManager;
        const entities = scene.entities.filter((entity) => this.getProductType(entity) === productType);
        return entities;
    }
    /**
     * @param productTypes Types to filter by
     * @returns all entities with provided product types
     */
    getByProductTypes(productTypes) {
        return productTypes.flatMap(type => {
            return this.getByProductType(type);
        });
    }
    /**
     *
     * @param entity entity in which parents to search
     * @param productType list of product types to match
     * @returns list of parents whose product type matches the passed product type
     */
    getAncestorsWithProductType(entity, productType) {
        return this.getAncestorsWithOneOfProductTypes(entity, [productType]);
    }
    /**
     *
     * @param entity entity in which parents to search
     * @param productTypes list of product types to match
     * @returns list of parents whose product type matches at least on of the passed product types
     */
    getAncestorsWithOneOfProductTypes(entity, productTypes) {
        let parent = entity.getComponent(ComponentIds.CHILD_COMPONENT_ID)?.parent;
        const ancestors = [];
        while (parent) {
            if (this.isOneOfProductTypes(parent, productTypes)) {
                ancestors.push(parent);
            }
            parent = parent.getComponent(ComponentIds.CHILD_COMPONENT_ID)?.parent;
        }
        return ancestors;
    }
    /**
     *
     * @param entity entity in which parents to search
     * @param componentId component to match
     * @returns list of parents whose component matches componentIds
     */
    getAncestorsWithComponent(entity, componentId) {
        let parent = entity.getComponent(ComponentIds.CHILD_COMPONENT_ID)?.parent;
        const ancestors = [];
        while (parent) {
            if (parent.hasComponent(componentId)) {
                ancestors.push(parent);
            }
            parent = parent.getComponent(ComponentIds.CHILD_COMPONENT_ID)?.parent;
        }
        return ancestors;
    }
    /**
     *
     * @param entity entity in which descendants to search
     * @param productType product type to match
     * @returns descendant that matches the product types passed as parameter
     */
    findDescendantWithProductType(entity, productType) {
        return this.findDescendantWithOneOfProductTypes(entity, [productType]);
    }
    /**
     *
     * @param entity entity in which descendants to search
     * @param productTypes list of product types to match
     * @returns one descendant that matches at least one of the product types passed as parameter
     */
    findDescendantWithOneOfProductTypes(entity, productTypes) {
        return (HierarchyUtils.descendants(entity).find(e => this.isOneOfProductTypes(e, productTypes)) ||
            null);
    }
    /**
     *
     * @param entity entity in which descendants to search
     * @param productType product type to match
     * @returns all descendants that matches the product type passed as parameter
     */
    getDescendantsWithProductType(entity, productType) {
        return this.getDescendantsWithOneOfProductTypes(entity, [productType]);
    }
    /**
     *
     * @param entity entity in which descendants to search
     * @param productTypes list of product types to match
     * @returns all descendants that matches at least one of the product types passed as parameter
     */
    getDescendantsWithOneOfProductTypes(entity, productTypes) {
        return HierarchyUtils.descendants(entity).filter(e => this.isOneOfProductTypes(e, productTypes));
    }
    /**
     *
     * @param entity entity in which descendants to search
     * @param componentId id of component to search for
     * @returns all descendants with specified component
     */
    getDescendantsWithComponent(entity, componentId) {
        return HierarchyUtils.descendants(entity).filter((entity) => entity.hasComponent(componentId));
    }
    /**
     * Used to find a child of entity that matches the product type
     * @param entity entity
     * @param productTypes product type
     * @returns one child matching the product type
     */
    findChildWithProductType(entity, productType) {
        return this.findChildWithOneOfProductTypes(entity, [productType]);
    }
    /**
     * Used to find a child of entity that matches of the product types
     * @param entity entity
     * @param productTypes list of product types
     * @returns one child matching one of the product types
     */
    findChildWithOneOfProductTypes(entity, productTypes) {
        return (HierarchyUtils.children(entity).find(child => this.isOneOfProductTypes(child, productTypes)) || null);
    }
    /**
     * Used to find direct decendants of entity by product type
     * @param entity parent entity
     * @param productType product type to search for
     * @returns list of children with matching product type
     */
    getChildrenWithProductType(entity, productType) {
        return this.getChildrenWithOneOfProductTypes(entity, [productType]);
    }
    /**
     * Used to find direct decendants of entity by product types
     * @param entity parent entity
     * @param productTypes list of product types
     * @returns list of children with matching one of the product types
     */
    getChildrenWithOneOfProductTypes(entity, productTypes) {
        return HierarchyUtils.children(entity).filter(child => this.isOneOfProductTypes(child, productTypes));
    }
    /**
     * This function will first look for matching entity in decendants, and if not found
     * search among the parents children
     * @param entity entity whos hierarch to search in
     * @param productType productType to match
     * @returns entity in hierarchy that matches @param productType
     */
    findEntityInHierarchyWithOneOfProductType(entity, productType) {
        return this.findEntityInHierarchyWithOneOfProductTypes(entity, [productType]);
    }
    /**
     * This function will first look for matching entity in decendants, and if not found
     * search among the parents children
     * @param entity entity whos hierarch to search in
     * @param productTypes list of productTypes to match
     * @returns entity in hierarchy that matches one of @param productTypes
     */
    findEntityInHierarchyWithOneOfProductTypes(entity, productTypes) {
        const entityInDescendants = this.findDescendantWithOneOfProductTypes(entity, productTypes);
        if (entityInDescendants)
            return entityInDescendants;
        const parent = HierarchyUtils.parent(entity);
        if (parent) {
            const entityInParentHierarchy = HierarchyUtils.children(parent).find((child) => this.isOneOfProductTypes(child, productTypes));
            return entityInParentHierarchy || null;
        }
        return null;
    }
    /**
     * @returns all entities in the scene that are products
     */
    getAll() {
        return this.getByComponent(PlannerProductComponent.id);
    }
    /**
     * @returns productId and entityId for all product entities in the scene
     */
    getAllProductEntities() {
        return this.getByComponent(PlannerProductComponent.id).map((entity) => ({
            entityId: entity.id,
            productId: entity.getComponent(PlannerProductComponent.id).productId
        }));
    }
    /**
     * @returns productId and entityId for all shoppingbag entities in the scene
     */
    getAllShoppingBagEntities() {
        return this.getByComponent(ShoppingBagComponent.id).map((entity) => ({
            entityId: entity.id,
            productId: entity.getComponent(ShoppingBagComponent.id).productId
        }));
    }
    /**
     * Finds the selected entity
     * @returns selected entity or null if no entity is selected
     */
    findSelectedEntity() {
        const entities = this.getByComponent(ComponentIds.SELECTED_COMPONENT_ID);
        return entities.length === 1 ? entities[0] : null;
    }
    /**
     * @return productId, entityId, position and rotation for all entities in the scene
     */
    getAllWithPosition() {
        return this.getByComponent(PlannerProductComponent.id)
            .map((entity) => {
            const coord3D = entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID)
                ? MeasurementUtils.getOutsideMeasurementSingle(entity)?.center
                : TransformUtils.worldPosition(entity);
            if (!coord3D) {
                return null;
            }
            const coord2D = this.core.services.coreModules
                .viewer()
                .worldCoordToCanvas(coord3D.X, coord3D.Y, coord3D.Z);
            return {
                entityId: entity.id,
                productId: entity.getComponent(PlannerProductComponent.id)
                    .productId,
                worldPosition: coord3D,
                canvasPosition: coord2D
            };
        })
            .filter(Boolean);
    }
    /**
     * @param component component to filter by
     */
    showEntities(componentIds, traverseHierarchy = false) {
        const entities = this.getByComponents(componentIds);
        entities.forEach(entity => {
            ModelUtils.show(entity, traverseHierarchy);
        });
    }
    /**
     * @param component component to filter by
     */
    hideEntities(componentIds, traverseHierarchy = false) {
        const entities = this.getByComponents(componentIds);
        entities.forEach(entity => {
            ModelUtils.hide(entity, traverseHierarchy);
        });
    }
    /**
     *
     * @param entity the entity that will change visibility
     * @param visible true if visible and false if hidden
     * @param traverseHierarchy
     */
    setVisible(entity, visible, traverseHierarchy = false) {
        visible
            ? ModelUtils.show(entity, traverseHierarchy)
            : ModelUtils.hide(entity, traverseHierarchy);
    }
    /**
     *
     * @param entities the entities that will change visibility
     * @param visible true if visible and false if hidden
     * @param traverseHierarchy
     */
    setAllVisible(entities, visible, traverseHierarchy = false) {
        entities
            .filter(e => !e.hasComponent(SeeThroughFrontComponent.id))
            .forEach(entity => {
            this.setVisible(entity, visible, traverseHierarchy);
        });
    }
    /**
     * @param entity
     * @returns provided entity with position and productId
     */
    getWithPosition(entity) {
        const coord3D = entity.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID).transform.position;
        const coord2D = this.core.services.coreModules
            .viewer()
            .worldCoordToCanvas(coord3D.X, coord3D.Y, coord3D.Z);
        if (!coord2D) {
            return null;
        }
        return {
            productId: entity.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)
                ?.productRef,
            position: { x: coord2D.X, y: coord2D.Y },
            entityId: entity.id
        };
    }
    /**
     * @param entityId
     * @returns entity with position and productId or undefined if entity is not found
     */
    getWithPositionById(entityId) {
        const scene = this.core.services.sceneManager;
        const entity = scene.findEntity(entityId);
        if (entity) {
            return this.getWithPosition(entity);
        }
    }
    /**
     * @param entity
     * @returns provided entity with position and productId
     */
    getEntityMenuPosition(entity) {
        const bounds = this.getBoundingBox([entity]);
        if (!bounds)
            return null;
        const wall = this.getEntityWall(entity);
        let point = new Vector(bounds.max.x, (bounds.max.y + bounds.min.y) / 2, bounds.max.z).times(1000);
        switch (wall) {
            case 'back': {
                point = new Vector(bounds.min.x, (bounds.max.y + bounds.min.y) / 2, bounds.min.z).times(1000);
                break;
            }
            case 'left': {
                point = new Vector(bounds.max.x, (bounds.max.y + bounds.min.y) / 2, bounds.min.z).times(1000);
                break;
            }
            case 'right': {
                point = new Vector(bounds.min.x, (bounds.max.y + bounds.min.y) / 2, bounds.max.z).times(1000);
                break;
            }
        }
        const coord2D = point &&
            this.core.services.coreModules.viewer().worldCoordToCanvas(point.X, point.Y, point.Z);
        if (!coord2D) {
            return null;
        }
        return {
            productId: entity.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)
                ?.productRef,
            position: { x: coord2D.X, y: coord2D.Y },
            entityId: entity.id
        };
    }
    /**
     * @param entityId id of entity to find
     * @returns entityId and productId or null if neither is found
     */
    findProductEntity(entityId) {
        const scene = this.core.services.sceneManager;
        const coreEntity = scene.findEntity(entityId);
        if (!coreEntity)
            return null;
        const product = coreEntity.getComponent(ComponentIds.PRODUCT_COMPONENT_ID);
        if (!product)
            return null;
        return {
            entityId,
            productId: product.productRef
        };
    }
    /**
     * @param entityId id of entity to find
     * @returns entity
     */
    find(entityId) {
        const scene = this.core.services.sceneManager;
        return scene.findEntity(entityId);
    }
    /**
     *
     * @param entities entities to search in
     * @param partKey partkey to look for
     * @returns an entity where the partkey matches
     */
    findEntityWithPartKey(entities, partKey) {
        return (entities.find(entity => entity.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID) &&
            entity.getComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
                ?.partKey === partKey) || null);
    }
    /**
     *
     * @param entities entities to search in
     * @param partKey partkey to look for
     * @returns list of entities that matches the partkey
     */
    getEntitiesWithPartKey(entities, partKey) {
        return entities.filter(entity => entity.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID) &&
            entity.getComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
                ?.partKey === partKey);
    }
    /**
     *
     * @param entity entity to check
     * @param partKey partkey to check
     * @returns boolean indicating if entity is product part with speceified part key
     */
    hasPartKey(entity, partKey) {
        return (entity.getComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)?.partKey ===
            partKey);
    }
    /**
     *
     * @param productId product id of the new entity
     * @returns the newly created entity
     */
    create(productId) {
        const entity = this.core.services.productManager.createEntity(productId);
        return entity;
    }
    /**
     * @param childEntity entity which is part of an assembly
     * @returns assembly of entity passed as parameter
     */
    getAssemblyRoot(childEntity) {
        let entity = childEntity;
        while (entity != null) {
            if (entity.hasComponent(ComponentIds.CONNECTION_ROOT_COMPONENT)) {
                return entity;
            }
            entity = entity.getComponent(ComponentIds.CHILD_COMPONENT_ID)?.parent || null;
        }
        return null;
    }
    /**
     * @param entity
     * @returns list of child entities of given entity
     */
    getDescendants(entity) {
        return HierarchyUtils.descendants(entity).reduce((acc, child) => {
            if (child.hasComponent(ComponentIds.PRODUCT_COMPONENT_ID)) {
                return [
                    ...acc,
                    {
                        entityId: child.id,
                        productId: child.getComponent(ComponentIds.PRODUCT_COMPONENT_ID).productRef
                    }
                ];
            }
            return acc;
        }, []);
    }
    /**
     * Returns all descendants to a frame but doesn't include stacked frames and their descendants
     * @param frame frame entity to get descendants for
     * @returns list of frame decendants
     */
    getFrameDescendants(frame) {
        const getEntityDescendants = (entity) => {
            const children = HierarchyUtils.children(entity);
            return children.reduce((acc, child) => {
                if (child.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
                    !child.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)) {
                    // If we come to a child that is a frame (and not part of an assembly, e.g. in corner solution)
                    // we stop the search for recursive children
                    return acc;
                }
                // Return child and continue to search for childs children
                return [...acc, child, ...getEntityDescendants(child)];
            }, []);
        };
        return getEntityDescendants(frame);
    }
    /**
     * @param entity
     * @returns parent to a given entity
     */
    getParent(entity) {
        return HierarchyUtils.parent(entity);
    }
    /**
     *
     * @param entity
     * @returns viewer object id
     */
    getObjectId(entity) {
        return (entity
            .getComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
            ?.viewerObject.getViewerObjectId() || null);
    }
    /**
     * @param entities
     * @returns viewer object id for all @param entities
     */
    getObjectIds(entities) {
        return entities
            .map((entity) => this.getObjectId(entity))
            .filter((id) => id !== null);
    }
    /**
     *
     * @param entities
     * Finds all related viewer object ids from a list of entities
     */
    findAllRelatedObjectIds(entities, includePropping = false) {
        const objectIds = [];
        const traverseEntityTree = (entityTree) => {
            entityTree.forEach((entity) => {
                const hasParentComponent = entity.hasComponent(ComponentIds.PARENT_COMPONENT_ID);
                const hasModel = entity.hasComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID);
                const hasProppingComponent = entity.hasComponent(ComponentIds.PROPPING_COMPONENT_ID);
                const hasFrameComponent = entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID);
                const hasLegComponent = entity.hasComponent(ComponentIds.LEG_COMPONENT_ID);
                if (hasProppingComponent && hasModel && includePropping) {
                    const objectId = entity
                        .getComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
                        ?.viewerObject?.getViewerObjectId();
                    if (objectId)
                        objectIds.push(objectId);
                    return;
                }
                if (hasModel && !hasProppingComponent && !hasFrameComponent && !hasLegComponent) {
                    const objectId = entity
                        .getComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
                        ?.viewerObject?.getViewerObjectId();
                    if (objectId)
                        objectIds.push(objectId);
                    return;
                }
                if (hasParentComponent) {
                    // We only traverese if the first child of the parent array is not a frame component, this means that it's a corner solution.
                    // We might have to change this in the future
                    const children = HierarchyUtils.children(entity);
                    const firstChildIsFrame = children.length > 0 && children[0].hasComponent(ComponentIds.FRAME_COMPONENT_ID);
                    if (!firstChildIsFrame && children) {
                        traverseEntityTree(children);
                    }
                }
            });
        };
        traverseEntityTree(entities);
        return objectIds.flat().filter(Boolean);
    }
    /**
     *
     * @param entities
     * Finds all related frame entities
     */
    findAllRelatedFrames(entity) {
        const frameEntities = [];
        function traverseEntityTreeAndFindFrames(entityTree) {
            entityTree.forEach((e) => {
                if (e.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
                    frameEntities.push(e);
                }
                if (e.hasComponent(ComponentIds.PARENT_COMPONENT_ID)) {
                    traverseEntityTreeAndFindFrames(e.getComponent(ComponentIds.PARENT_COMPONENT_ID).children);
                }
            });
        }
        if (entity.hasComponent(ComponentIds.PARENT_COMPONENT_ID)) {
            traverseEntityTreeAndFindFrames(entity.getComponent(ComponentIds.PARENT_COMPONENT_ID).children);
        }
        return frameEntities.flat();
    }
    /**
     *
     * @param entities
     * Finds all related entityIds from a list of entities
     */
    findAllRelatedEntityIds(entities) {
        const entityIds = [];
        function traverseEntityTree(entityTree) {
            entityTree.forEach((entity) => {
                entityIds.push(entity.id);
                if (entity.hasComponent(ComponentIds.PARENT_COMPONENT_ID)) {
                    traverseEntityTree(entity.getComponent(ComponentIds.PARENT_COMPONENT_ID).children);
                }
            });
        }
        traverseEntityTree(entities);
        return entityIds.flat();
    }
    /**
     * @returns the outer measurements the provided entities
     */
    getMeasurements(entities, withHierachy = false) {
        const outsideMeasurements = withHierachy
            ? MeasurementUtils.getOutsideMeasurement(entities)
            : MeasurementUtils.getOutsideMeasurementWithoutHierarchies(entities);
        if (!outsideMeasurements) {
            return null;
        }
        return transformOuterMeasurements(outsideMeasurements);
    }
    /**
     * @returns viewer object id for any entity
     */
    getObjectIdsByEntityId(entityId) {
        const entity = this.find(entityId);
        if (!entity) {
            return [];
        }
        return this.getObjectIdsByEntity(entity);
    }
    /**
     * @returns viewer object id for any entity
     */
    getObjectIdsByEntity(entity) {
        if (entity.hasComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)) {
            const objectId = this.getObjectId(entity);
            return objectId ? [objectId] : [];
        }
        else if (HierarchyUtils.isAssembly(entity)) {
            const assemblyParts = HierarchyUtils.getAssemblyEntities(entity);
            return assemblyParts.map(child => this.getObjectId(child)).filter(Boolean);
        }
        return [];
    }
    /**
     * @returns world position for entity
     */
    getWorldPosition(entity) {
        const worldPosition = entity.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform.position;
        return worldPosition;
    }
    /**
     * @returns world rotation for entity
     */
    getWorldRotation(entity) {
        const worldRotation = entity.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform.rotation;
        return worldRotation;
    }
    /**
     *
     * @param entity
     * @returns which wall an entity is connected to
     */
    getEntityWall(entity) {
        let wallEntity = entity;
        if (entity.hasComponent(ComponentIds.LEG_COMPONENT_ID)) {
            const frameWithLegs = HierarchyUtils.getAncestors(entity).find(frame => frame.hasComponent(ComponentIds.FRAME_COMPONENT_ID));
            wallEntity = frameWithLegs || entity;
        }
        const rotation = wallEntity
            .getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)
            ?.transform?.rotation?.toEulerDegrees();
        if (rotation && (Math.round(rotation.Y) === 180 || Math.round(rotation.Y) === -180)) {
            return 'back';
        }
        if (rotation && Math.round(rotation.Y) === 90) {
            return 'left';
        }
        else if (rotation && Math.round(rotation.Y) === -90) {
            return 'right';
        }
        return 'front';
    }
    /**
     *
     * @param entityId
     * @returns Finds all related viewer object ids for an entity
     */
    getRelatedObjectIds(entityId, includePropping = false) {
        const entity = this.find(entityId);
        if (!entity) {
            return [];
        }
        const relatedObjectIds = this.findAllRelatedObjectIds([entity], includePropping);
        return relatedObjectIds;
    }
    isEntityConnectorsAligned(entity1, connectorKey1, entity2, connectorKey2) {
        const isAligned = (position1, position2) => {
            if (!position1 || !position2)
                return false;
            return (Math.round(position1.X) === Math.round(position2.X) &&
                Math.round(position1.Y) === Math.round(position2.Y) &&
                Math.round(position1.Z) === Math.round(position2.Z));
        };
        const connector1 = entity1
            .getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
            ?.connectors.find(c => c.key === connectorKey1);
        const connector1Position = connector1?.getWorldPositionFromOwnerEntity(entity1);
        const connector2 = entity2
            .getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
            ?.connectors.find(c => c.key === connectorKey2);
        const connector2Position = connector2?.getWorldPositionFromOwnerEntity(entity2);
        return isAligned(connector1Position, connector2Position);
    }
    /**
     * Get aligned frames in same depth and height
     * @param frame
     * @returns left and right aligned frames (or null if none)
     */
    getAlignedFrames(frame, leftAlignmentConnectorKey, rightAlignmentConnectorKey, matchHeigth, matchDepth) {
        const isSameDepth = (frame1, frame2) => {
            return frame1.id !== frame2.id
                ? this.getEntityComSize(frame1)?.depth === this.getEntityComSize(frame2)?.depth
                : false;
        };
        const isSameHeight = (frame1, frame2) => {
            return frame1.id !== frame2.id
                ? this.getEntityComSize(frame1)?.height === this.getEntityComSize(frame2)?.height
                : false;
        };
        const leftAlignedFrame = this.getByComponent(ComponentIds.FRAME_COMPONENT_ID)
            .filter(f => (matchHeigth ? isSameHeight(frame, f) : true))
            .filter(f => (matchDepth ? isSameDepth(frame, f) : true))
            .find(f => this.isEntityConnectorsAligned(frame, leftAlignmentConnectorKey, f, rightAlignmentConnectorKey)) || null;
        const rightAlignedFrame = this.getByComponent(ComponentIds.FRAME_COMPONENT_ID)
            .filter(f => (matchHeigth ? isSameHeight(frame, f) : true))
            .filter(f => (matchDepth ? isSameDepth(frame, f) : true))
            .find(f => this.isEntityConnectorsAligned(f, leftAlignmentConnectorKey, frame, rightAlignmentConnectorKey)) || null;
        return { left: leftAlignedFrame, right: rightAlignedFrame };
    }
    /**
     *
     * @param entity
     * @returns true if entity is connected to the wall. False otherwise
     */
    isEntityPlacedOnWall(entity) {
        const connection = entity.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)?.highestPriorityConnection;
        return connection ? RoomConnectorConstants.hasWallPlacement(connection) : false;
    }
    /**
     *
     * @param entity
     * @returns true if entity is connected to the floor. False otherwise
     */
    isEntityPlacedOnFloor(entity) {
        const connection = entity.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)?.highestPriorityConnection;
        return connection ? RoomConnectorConstants.hasFloorPlacement(connection) : false;
    }
    /**
     * Check if frame is placed on floor or part of stack that is correctly stacked on floor
     * @param frame frame entity
     * @returns true if frame is placed on floor or correctly stacked on frames that are floor standing
     */
    isPartOfFloorstandingStack(frame) {
        if (this.isEntityPlacedOnFloor(frame)) {
            return true;
        }
        const frameStackingConnectorKeys = frame
            .getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
            ?.connectors.filter(connector => connector.tags().includes('stacking-guest'))
            .map(connector => connector.key) || [];
        const usedStackingConnections = frame
            .getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)
            ?.connections.filter(connection => frameStackingConnectorKeys.includes(connection.guestConnector.key)) || [];
        if (frameStackingConnectorKeys.length !== usedStackingConnections.length) {
            // Wide frame is only partially stacked on other frame
            return false;
        }
        const areHostsPartOfFloorstandingStacks = usedStackingConnections.map(connection => this.isPartOfFloorstandingStack(connection.hostConnectorEntity));
        return areHostsPartOfFloorstandingStacks.every(host => host === true);
    }
    /**
     *
     * @param position position to query
     * @param entities candidate entities
     * @returns entity closest to position
     */
    findClosestToPosition(position, entities) {
        // If we have no entities to search through, we return null
        if (!entities || entities.length === 0)
            return null;
        const entities2DPosition = entities.reduce((entitiesWithPosition, entity) => {
            const entityPosition = this.getWithPosition(entity);
            if (entityPosition) {
                return [
                    ...entitiesWithPosition,
                    {
                        entityId: entityPosition.entityId,
                        x: entityPosition.position.x,
                        y: entityPosition.position.y
                    }
                ];
            }
            return entitiesWithPosition;
        }, []);
        if (!entities2DPosition || entities2DPosition.length === 0)
            return null;
        const closestPositionIn2D = entities2DPosition.reduce((currentShortestPosition, nextPosition) => {
            const nextDistance = calculateDistanceBetweenPositions(position, nextPosition);
            const currentShortestDistance = calculateDistanceBetweenPositions(position, currentShortestPosition);
            if (nextDistance < currentShortestDistance) {
                return nextPosition;
            }
            else {
                return currentShortestPosition;
            }
        });
        const closestToPositon = entities.find(entity => entity.id === closestPositionIn2D.entityId);
        return closestToPositon || null;
    }
    /**
     *
     * @param position position to query
     * @param componentId component id to filter which entities to look for
     * @returns entity closest to position
     */
    getEntityClosestToPosition(position, componentIds) {
        const entities = this.getByComponents(componentIds);
        return this.findClosestToPosition(position, entities);
    }
    /**
     *
     * @param position position to query
     * @param componentId component id to filter which entities to look for
     * @returns entity at position or null if no entities found on position
     */
    getEntityAtPosition(position, componentIds) {
        const entities = this.getByComponents(componentIds);
        const entitiesObjectIds = entities.flatMap(entity => this.getObjectIdsByEntity(entity));
        const raycastedPosition = this.ipexViewer.raycast_object_using_canvas_coordinate(position.x, position.y, entitiesObjectIds);
        const entityAtPosition = entities.find(e => {
            const objectIds = this.getObjectIdsByEntity(e);
            return raycastedPosition?.object && objectIds.includes(raycastedPosition?.object);
        });
        if (!entityAtPosition)
            return null;
        return entityAtPosition.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
            ? this.getAssemblyRoot(entityAtPosition)
            : entityAtPosition;
    }
    /**
     *
     * @param objectId viewer id
     * @param componentId component ids to filter which entities to look for
     * @returns entity with object id or undefined if nothing found
     */
    findEntityWithObjectId(objectId) {
        const scene = this.core.services.sceneManager;
        return scene.entities.find(e => this.getObjectId(e) === objectId);
    }
    /**
     * Get entity comunicated size
     * @param entity the entity to get size of
     * @returns the entitys size
     */
    getEntityComSize(entity) {
        return entity.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
    }
    disposeCollidingEntities(errors, self = true) {
        if (!errors)
            return;
        errors?.forEach(error => {
            const collisionError = error;
            if ('collisions' in error) {
                collisionError.collisions.forEach(collision => {
                    const entity = self ? collision.self : collision.other;
                    if (!entity.isDisposed) {
                        this.core.services.entityManager.disposeEntity(entity);
                    }
                });
            }
        });
    }
    isMirrored(entity) {
        const mirrored = entity
            .getComponent(ComponentIds.PRODUCT_COMPONENT_ID)
            ?.productRef.includes(ProductIdentifiers.MIRRORED_PRODUCT);
        return !!mirrored;
    }
    resnapEntity(entity) {
        const worldPosition = TransformUtils.worldPosition(entity);
        if (!worldPosition)
            return;
        return this.placementService.snapToPosition(entity, worldPosition, true);
    }
    /**
     *
     * @param entity entity inside frame to raycast from
     * @returns raycast hit (if any) with front entity which covers the input entity
     */
    getCoveringFronts(entity) {
        const entityWidth = this.getEntityComSize(entity)?.width || 0;
        const entityTransform = entity.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform;
        // Spread the rays
        const positions = [-entityWidth / 4, 0, entityWidth / 4].map(offset => entityTransform.position.plus(entityTransform.transformVector(new Vector(offset, 0, 0))));
        let hits = [];
        const entityFrame = HierarchyUtils.findWithComponentInUpwardsHierarchy(entity, ComponentIds.FRAME_COMPONENT_ID);
        positions.forEach(pos => {
            const currHits = this.getEntitiesInDirection(pos, new Vector(0, 0, 1), entityFrame ?? HierarchyUtils.rootOf(entity));
            hits = hits.concat(currHits);
        });
        const uniqueFrontHits = hits
            .filter(hit => hit.entity.hasComponent(ComponentIds.DOOR_FRONT_COMPONENT_ID))
            .reduce((acc, curr) => (acc.some(hit => hit.entity.id === curr.entity.id) ? acc : [...acc, curr]), []);
        return uniqueFrontHits;
    }
    /**
     *
     * @param point point in space
     * @param dir directional vector for raycast
     * @param rootEntity parent entity to use as root.
     * If passed, root entity transform will be applied to directional vector
     * @returns all entity hits in given direction
     */
    getEntitiesInDirection(point, dir, relativeTo) {
        let direction;
        let hits = [];
        const root = this.getScene();
        if (relativeTo) {
            // Apply root entity transform to direction vector
            const rootTransform = relativeTo.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID);
            direction = rootTransform?.transform?.transformVector(dir);
        }
        else {
            direction = dir;
        }
        if (!direction)
            direction = dir;
        const ray = new Ray(point, direction);
        hits = hits.concat(RaycastUtils.raycastAll(ray, root));
        const uniqueEntityHits = hits
            .filter(hit => hit.entity.hasComponent(ComponentIds.PRODUCT_COMPONENT_ID))
            .reduce((acc, curr) => (acc.some(hit => hit.entity.id === curr.entity.id) ? acc : [...acc, curr]), []);
        return uniqueEntityHits;
    }
    rotate(entity) {
        throw new Error(`Rotate not implemented for ${entity}`);
    }
}
//# sourceMappingURL=EntityApi.js.map